import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  dictionary,
  LOCALES,
  type Locale,
  type Translation,
} from './translations';

const STORAGE_KEY = 'rai_locale';

interface LanguageContextValue {
  locale: Locale;
  t: Translation;
  setLocale: (locale: Locale) => void;
}

const Ctx = createContext<LanguageContextValue | null>(null);

function detectInitialLocale(): Locale {
  if (typeof window === 'undefined') return 'pt';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY) as Locale | null;
    if (stored && LOCALES.includes(stored)) return stored;
  } catch {
    // ignore
  }
  const navLang = window.navigator.language.toLowerCase();
  if (navLang.startsWith('en')) return 'en';
  if (navLang.startsWith('es')) return 'es';
  return 'pt';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectInitialLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale === 'pt' ? 'pt-BR' : locale;
    }
  }, [locale]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      locale,
      t: dictionary[locale],
      setLocale,
    }),
    [locale, setLocale]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLanguage() {
  const ctx = useContext(Ctx);
  if (!ctx)
    throw new Error('useLanguage precisa estar dentro de LanguageProvider');
  return ctx;
}

/** Atalho: hook que retorna apenas o objeto de traducoes */
export function useT(): Translation {
  return useLanguage().t;
}
